import { Component, Input } from '@angular/core';
import Highcharts from 'highcharts';
import { ChartPoint, ChartSeries, chartColors } from '../report-aggregation';
import { ChartBlockBase } from './chart-block-base';
import type { BlockPatch } from '../report-design-edit';
import {
  Band,
  BandSpec,
  bandScale,
  bandsFromBoxes,
  bandsOf,
  netOfBands,
  shareOfBand,
} from './scale-bands';
import {
  CHART_SPACING,
  CHART_V_SPACING,
  LEGEND_ITEM_WIDTH,
  categoryAxis,
  categoryLabelFormatter,
  categoryLabelStyle,
  clampedCategories,
  BAR_BORDER_RADIUS,
  BAR_GAP_PX,
  BAR_LABEL_GUTTER_PX,
  BAR_THICKNESS_PX,
  barLabelColumn,
  labelFitFor,
  legendHeightFor,
  textWidth,
} from './chart-layout';

/**
 * Two pixels over the deck's plain bar, and a deliberate break from the one-geometry rule the rest
 * of the bar family keeps. A stacked bar carries its figures *inside* its segments rather than at
 * one end, so it is the only bar in the deck whose height has to hold text.
 */
const BAR_PX = BAR_THICKNESS_PX + 2;

/**
 * A single bar is a different drawing from a battery of them.
 *
 * With several, the shape of each against the others is the finding and a row is a row. Alone --
 * one trajectory question, one distribution under a score -- there are no other bars to compare it
 * with, so the bar is the whole module, and drawn at a row's height it reads as a battery someone
 * forgot to finish. Wide enough here to carry its segment figures comfortably and to hold the card
 * on its own.
 */
const SOLO_BAR_PX = 59;
/** Below this share of the bar a segment has no room for its own percentage. */
const MIN_LABELLED_PERCENT = 6;

/** Options counted at each end of an older, unbanded block's scale when it does not say. */
const DEFAULT_NET_BOXES = 2;

/**
 * Gap between the legend and the top of the plot. The net heading sits on the legend's own line
 * rather than in a band below it, so there is nothing here to leave room for.
 */
const LEGEND_MARGIN = 14;

/** Where the net heading falls back to when there is no legend item to take its line from. */
const NET_TITLE_LIFT = 12;

/**
 * The band opened above the plot for the net heading when the legend is not up there to share a
 * line with. Only as tall as the heading and its lift, so the first bar stays close under it.
 */
const NET_HEAD_BAND = 22;

/** Highcharts' default legend padding, which sits between the legend box and its first key. */
const LEGEND_PADDING = 8;

/**
 * The gap between keys once they are left-aligned under the bars. Fixed rather than spread across
 * the row: spread out from a left edge, the keys read as a justified line with holes in it.
 */
const BELOW_LEGEND_GAP = 20;

/** The key and the net heading read as one row, so they share a size. */
const LEGEND_FONT_PX = 12;
/** A square key, not the pill Highcharts draws when symbolRadius is left to default to half. */
const LEGEND_SYMBOL_PX = 12;
/** Highcharts' own gap between a key and the text it belongs to. */
const LEGEND_SYMBOL_PADDING = 5;
/**
 * Bounds on the gap between one legend item and the next. The gap is whatever is left of the row
 * once the text has taken what it needs -- so a short scale on a wide card spreads out and a long
 * one closes up, but never so far apart that two keys stop reading as one row, and never so close
 * that one item's text runs into the next one's square.
 */
const MIN_LEGEND_GAP = 14;
const MAX_LEGEND_GAP = 44;

/**
 * Where a net stops reading as a bad number and starts reading as a good one. A net is a difference
 * in percentage points, so 50 means the top boxes outweigh the bottom boxes by half the sample --
 * strong, rather than merely positive. It is a convention and not a fact about the data, which is
 * why `netThreshold` can move it.
 */
const DEFAULT_NET_THRESHOLD = 50;
/** Chip backgrounds: --nav-success and --nav-error lifted to a tint the figure still reads on. */
const NET_GOOD_BG = '#eaf7ea';
const NET_BAD_BG = '#fbeaea';
/** Wide enough that a three-digit net and a signed two-digit one are the same block. */
const NET_CHIP_WIDTH_PX = 62;
/** Clear air outside the whole thing: before the statements, and after the net column. */
const SIDE_PAD = 24;
/** Between the end of a statement and the start of its bar. */
const LABEL_GUTTER = 14;
/** Between the end of the bars and the net chips, so the column reads as its own thing. */
const NET_COLUMN_GAP = 22;
/** Squared off, near enough: a chip reads as the end of its bar rather than as a pill beside it. */
const NET_CHIP_RADIUS_PX = 3;
/** The share's figure is bold and was measured at the regular weight, so it is given a little over. */
const SHARE_WIDTH_ALLOWANCE = 8;


@Component({
  selector: 'app-stacked-distribution-block',
  standalone: false,
  template: `
    <!-- A single bar's scores, headlined over it: the figure large and its caption under it, side
         by side when there is more than one. First, because on one bar the figure is the finding and
         the bar is how it breaks down. -->
    @if (headlines.length) {
      <div class="headlines d-flex flex-wrap mb-2">
        <!-- By position: the share's caption is typed into while editing, and tracked by its own
             text it would be redrawn -- and lose the cursor -- on every key. -->
        @for (headline of headlines; track $index) {
          <p class="headline mb-0">
            <span class="figure" [style.color]="headline.colour">{{ headline.figure }}</span>
            @if (editing && headline.placeholder !== undefined) {
              <input type="text" class="ecr-inline-input caption" maxlength="120"
                     aria-label="Caption under the share" [placeholder]="headline.placeholder"
                     [value]="headline.text ?? ''"
                     (input)="setShareCaption($any($event.target).value)">
            } @else {
              <span class="caption">{{ headline.caption }}</span>
            }
          </p>
        }
      </div>
    }
    <!-- One bar puts its question above the chart instead of in a column beside it. A label column
         exists so a battery's statements line up with each other; with nothing to line up against it
         is half the card spent on one sentence, and the bar it belongs to has nowhere left to go.
         Above the key, because the key describes the bar and the question describes the key. -->
    @if (soloLabel) {
      <p class="solo-label mb-2">{{ soloLabel }}</p>
    }
    @if (chartOptions) {
      <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                        style="width: 100%; display: block;"></highcharts-chart>
    }
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [`
    :host { display: block; }
    .solo-label {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-sm, 12px);
      line-height: 1.35;
    }
    .headlines { gap: 8px 40px; }
    .headline { max-width: 22rem; }
    .headline .figure {
      display: block;
      font-size: 36px;
      font-weight: 700;
      line-height: 1.1;
    }
    .headline .caption {
      display: block;
      margin-top: 2px;
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-sm, 12px);
      font-weight: 600;
      line-height: 1.35;
    }
  `]
})
export class StackedDistributionBlockComponent extends ChartBlockBase {
  highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  /** The question, printed above a chart that draws only one bar. Blank otherwise. */
  soloLabel = '';
  /**
   * A single bar's net and sum, headlined above it. Empty on a battery, which has the column. The
   * share's caption is written in place while editing: `text` is it as stored and `placeholder`
   * what is shown when it is blank. The net's is built from its word, set in the panel.
   */
  headlines: { figure: string; caption: string; colour: string; text?: string; placeholder?: string }[] = [];

  /** Set while the deck is in edit mode, when a single bar's share caption is written in place. */
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  setShareCaption(caption: string) {
    this.onPatch?.({ shareCaption: caption });
  }

  protected build() {
    if (this.block.reportType !== 'stacked-scale') return;
    const known = this.series.filter(s => s.known);
    if (!known.length) {
      this.chartOptions = null;
      this.headlines = [];
      return;
    }

    const extra = this.block.reportExtraData;
    const hideLabels = !!extra.hideLabels;
    const labelSize = this.labelSize();

    // The scale comes from whichever question has the most options: a battery shares its answer
    // list, and taking the first would drop a segment if that one question happened to miss one.
    const spine = known.reduce(
      (widest, s) => s.points.length > widest.points.length ? s : widest, known[0]).points;

    // The scale, in order, minus anything that is not on it -- a trailing "Not sure" occupies its
    // share of the bar but is not the bottom of the scale, and netting it as though it were is the
    // easiest way to publish a wrong figure.
    const scale = bandScale(spine, extra.netExclude);

    // Which end of the asked order the favourable answers sit at. A survey lists its scale either
    // way round and the datamap does not record which, so this is set rather than inferred -- and
    // read the wrong way it does not degrade, it inverts: +67 publishes as -67.
    //
    // Bands where the block has them, the same vocabulary as the dial and the bar chart; otherwise
    // the older symmetric box count, turned into the bands it always meant, so a design written
    // before bands still computes the figures it did.
    const banded = !!extra.bands?.length;
    const topFirst = (banded ? extra.topBand : extra.netTopBox) !== 'last';
    const spec = banded
      ? extra.bands
      : bandsFromBoxes(scale.length, extra.netBoxes ?? DEFAULT_NET_BOXES, topFirst);
    // Null where the bands do not fit this scale, which blanks every score rather than inventing one.
    const spineBands = bandsOf(scale, spec);
    const showNet = !!extra.showNet && (spineBands?.length ?? 0) >= 2;

    // The bars, in the order they are drawn. Ranking the bars is not the same thing as ranking the
    // answers inside one, which this module must never do -- the scale above is untouched either
    // way. `sort` is stable, so two statements on the same net keep the order they were listed in.
    const order = extra.barOrder ?? 'asked';
    const score = order.startsWith('share')
      ? (s: ChartSeries) => this.shareOf(s, scale, spec, topFirst, extra.shareBand)?.share ?? 0
      : (s: ChartSeries) => this.netOf(s, scale, spec, topFirst) ?? 0;
    const drawn = order === 'asked'
      ? known
      : [...known].sort((a, b) => {
          const gap = score(b) - score(a);
          return order.endsWith('low') ? -gap : gap;
        });

    // One bar per question, one stacked segment per answer option.
    const categories = drawn.map(s => s.name);
    const threshold = Number.isFinite(extra.netThreshold as number)
      ? extra.netThreshold as number
      : DEFAULT_NET_THRESHOLD;

    const roles = chartColors(this.palette, Math.max(1, spine.length));

    // A single bar has no column for a net to head, so its scores are headlined above it instead --
    // the figure large, its caption under it -- and the column is drawn only for a battery.
    const solo = categories.length === 1;
    // One column down the right of a battery, the share or the net -- the share where both are set,
    // since two columns of figures beside one bar is a table, and the share was the one asked for.
    const shareColumn = !solo && !!extra.showShare && !!spineBands?.length;
    const netColumn = showNet && !solo && !shareColumn;
    const netHeading = extra.netLabel?.trim() ? `Net ${extra.netLabel.trim()}` : 'Net';
    this.headlines = [];
    if (solo && showNet) {
      const net = this.netFor(drawn[0], scale, spec, topFirst);
      this.headlines.push({
        figure: net,
        caption: netHeading,
        // The same reading as the column's chip: green at or above the threshold, red below.
        colour: Number(net) >= threshold ? 'var(--nav-success)' : 'var(--nav-error)',
      });
    }
    // One band on its own, counted from the good end -- the dial's "share", under the same name.
    const band = solo && extra.showShare
      ? shareOfBand(this.bandsFor(drawn[0], scale, spec) ?? [], topFirst, extra.shareBand)
      : null;
    if (band) {
      this.headlines.push({
        figure: `${Math.round(band.share)}%`,
        caption: extra.shareCaption?.trim() || band.label.trim() || band.range,
        text: extra.shareCaption ?? '',
        placeholder: band.label.trim() || band.range,
        // Ink rather than red or green: a share is a reading, and there is no threshold to judge it by.
        colour: 'var(--ecr-ink, #26414c)',
      });
    }

    // A single bar keeps its key centred above it, where it reads as the bar's own caption -- unless
    // a headline sits up there, when the key moves under the bar and leaves the headline its line.
    // Once there are several, the key moves to the bottom left, under the statements, and stops
    // sitting between the card's title and the rows it titles.
    const legendBelow = !solo || this.headlines.length > 0;
    const legendLeft = !solo;
    // Highcharts takes the legend's height off the plot wherever it sits. Count it into the chart's
    // own height or every bar gets thinner as the answer list gets longer.
    const legend = this.legendFit(spine.map(point => point.label), legendLeft ? BELOW_LEGEND_GAP : undefined);
    // The margin replaces Highcharts' default 12, which legendHeightFor already counts.
    const legendCost = legendHeightFor(legend.rows) + (legend.rows ? LEGEND_MARGIN - 12 : 0);
    // With the legend underneath, the net heading loses the line it shared and needs one of its own.
    const netBand = legendBelow && netColumn ? NET_HEAD_BAND : 0;
    // See CHART_V_SPACING: this was 40, and the 16px over the real spacing spread itself across
    // the gaps between the bars.
    const chrome = CHART_V_SPACING + legendCost + netBand;

    // One line, always: the row pitch is the bar plus its gap and nothing else, so a statement
    // that will not fit is cut rather than allowed to push the bars apart. The full text stays the
    // category, so the tooltip still carries it. This used to be a budget divided out of a capped
    // chart height, which is what let a twelve-statement chart draw its labels over its own bars.
    const fontFamily = this.fontFamily();
    const maxLines = 1;

    const fit = labelFitFor({
      plotWidth: this.plotWidth(),
      slots: categories.length,
      categories,
      labelSize,
      horizontal: true,
      horizontalWidth: barLabelColumn(this.plotWidth()) - BAR_LABEL_GUTTER_PX,
      fontFamily,
      maxLines,
    });
    // A question's text is a whole sentence, so this is the module where clamping bites hardest.
    const labelText = clampedCategories(categories, fit, labelSize, fontFamily);

    // Capped, not floored: this chart is always horizontal, and a minimum height would only
    // stretch the gaps between its bars, which are a flat thickness.
    // One bar alone is drawn several times over, so the slot and the height follow from it.
    const thickness = solo ? SOLO_BAR_PX : BAR_PX;
    const height = categories.length * (thickness + BAR_GAP_PX) + chrome;
    // The bar is a flat thickness now, so the chip simply matches it. It used to be read back off
    // the rendered height, because a bar sized by padding got thinner whenever the chart hit its
    // height cap and the chip had to follow it down.
    const chipHeight = thickness;

    // The question as the survey worded it, not the clamped version the axis would have drawn: it
    // has the card's width up here and nothing to line up with.
    this.soloLabel = categories.length === 1 && !hideLabels ? categories[0] : '';

    // Reserved by hand rather than left to Highcharts, which sizes the gutter to whatever the
    // labels happen to measure and leaves nothing outside them. The statements wrap inside a fixed
    // column, so the column's width is known here and everything else can be placed against it.
    //
    // A single bar reserves nothing: its question is printed above the chart instead, in full and
    // unclamped, so the bar can have the whole width.
    const asideLabels = !hideLabels && categories.length > 1;
    const labelWidth = asideLabels ? fit.wrapWidth : 0;
    const marginLeft = SIDE_PAD + (labelWidth ? labelWidth + LABEL_GUTTER : 0);
    // Each bar's share and the word after it, as the column prints them. Measured rather than
    // given a fixed slot like the chips, because the word is the author's and can be any length.
    const shares = shareColumn
      ? drawn.map(s => this.shareOf(s, scale, spec, topFirst, extra.shareBand))
      : [];
    const shareTexts = shares.map(band => shareText(band, extra.shareCaption));
    const shareWidth = shareColumn
      ? Math.max(0, ...shareTexts.map(text =>
          textWidth(`${text.figure} ${text.caption}`, LEGEND_FONT_PX, fontFamily))) + SHARE_WIDTH_ALLOWANCE
      : 0;
    const marginRight = SIDE_PAD + (netColumn ? NET_COLUMN_GAP + NET_CHIP_WIDTH_PX : 0)
      + (shareColumn ? NET_COLUMN_GAP + shareWidth : 0);

    this.chartOptions = this.deckMotion({
      chart: {
        type: 'bar',
        height,
        spacing: CHART_SPACING,
        marginLeft,
        marginRight,
        // Only when the net heading needs its band; otherwise the spacing and the legend decide.
        ...(netBand ? { marginTop: CHART_SPACING[0] + netBand } : {}),
        style: { fontFamily: 'inherit' },
        // Highcharts puts an axis title beyond that axis's labels, which for the net column means
        // beyond the right edge of the chart, where it is clipped away and never seen. Nothing in
        // the options can reach it -- `offset` moves the labels with it -- so it is placed here,
        // once the labels exist and their real width is known. Always bound, never conditional: an
        // options update merges, so a handler dropped from the options would stay attached anyway.
        events: { render: headNetColumn },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // Above a single bar, as its caption; below several, at the bottom left, lined up with the
      // start of the statements so it reads as the foot of the same column.
      legend: {
        enabled: true,
        align: legendLeft ? 'left' : 'center',
        verticalAlign: legendBelow ? 'bottom' : 'top',
        x: legendLeft ? SIDE_PAD - CHART_SPACING[3] - LEGEND_PADDING : 0,
        margin: LEGEND_MARGIN,
        // Highcharts defaults symbolRadius to half the symbol's height, which rounds the key into
        // a pill. Zero is the square.
        symbolRadius: 0,
        symbolHeight: LEGEND_SYMBOL_PX,
        symbolWidth: LEGEND_SYMBOL_PX,
        symbolPadding: LEGEND_SYMBOL_PADDING,
        // The one number that spreads the keys: each item is as wide as its own text, and whatever
        // is left of the row is divided between them.
        itemDistance: legend.gap,
        itemStyle: {
          fontSize: `${LEGEND_FONT_PX}px`,
          fontWeight: '400',
          color: 'var(--ecr-ink, #1b2b33)',
          // A cap rather than a width -- a short answer stays short, a paragraph is ellipsised.
          width: LEGEND_ITEM_WIDTH,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        },
        itemHoverStyle: { color: 'var(--ecr-ink, #1b2b33)' },
      },
      xAxis: [
        categoryAxis({
          categories,
          // Left-aligned against the outside edge rather than Highcharts' default of ragged-right
          // against the axis: a statement is a sentence, and a column of sentences that start in
          // different places is read one at a time instead of down the list. `x` is what puts the
          // block where the margin reserved it, since align only says which end the anchor is.
          labels: {
            step: 1,
            align: 'left',
            x: -(labelWidth + LABEL_GUTTER),
            formatter: categoryLabelFormatter(labelText),
            style: categoryLabelStyle(fit, labelSize),
          },
        }, asideLabels),
        // Linked to the category axis rather than positioned by hand: it mirrors the same
        // categories on the opposite side, so every net sits against its own bar however the plot
        // is sized. Always present and switched off instead of removed: an update merges, so an
        // axis dropped from the array would be left behind on the chart.
        {
          linkedTo: 0,
          opposite: true,
          visible: netColumn || shareColumn,
          // The index into shareTexts on a share column, so the formatter can find its caption.
          categories: shareColumn
            ? drawn.map((unused, at) => String(at))
            : drawn.map(s => this.netFor(s, scale, spec, topFirst)),
          lineWidth: 0,
          tickLength: 0,
          // Heads the column. `align: 'high'` is the top of a bar chart's category axis; the
          // render hook above then moves it over the figures and up into the band the widened
          // legend margin opens up.
          title: {
            // A share needs no heading: the word after every figure says what it is.
            text: shareColumn ? null : netHeading,
            align: 'high' as const,
            textAlign: 'right' as const,
            rotation: 0,
            offset: 0,
            y: -NET_TITLE_LIFT,
            style: {
              fontSize: `${LEGEND_FONT_PX}px`,
              fontWeight: '700',
              color: 'var(--ecr-ink, #1b2b33)',
            },
          },
          // HTML, because a chip is a background box around the figure and an SVG axis label has
          // nowhere to put one. The figure keeps its own colour inside the chip.
          labels: {
            step: 1,
            align: 'left',
            x: NET_COLUMN_GAP,
            useHTML: true,
            formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
              return shareColumn
                ? shareLabel(shareTexts[Number(this.value)], chipHeight)
                : netChip(String(this.value), threshold, chipHeight);
            },
            style: { fontSize: `${LEGEND_FONT_PX}px`, fontWeight: '600', color: 'var(--ecr-ink, #1b2b33)' },
          },
        },
      ],
      // Every bar is the whole of its question, so the axis is always 0-100 and printing it twice
      // -- once on the axis, once in each segment -- says nothing the segments do not.
      yAxis: {
        title: { text: undefined },
        min: 0,
        max: 100,
        labels: { enabled: false },
        gridLineWidth: 0,
        reversedStacks: false,
      },
      tooltip: {
        style: { width: 260, whiteSpace: 'normal' },
        pointFormat: '{series.name}: <b>{point.y:.0f}%</b> ({point.custom.count})',
      },
      plotOptions: {
        // A bar begins exactly on the plot's left edge, so the tip of its rounded start corner
        // falls on the clip boundary and gets shaved -- that end then reads as squarer than the
        // other. clip:false removes the boundary; nothing can overflow it anyway, since the axis
        // maximum is derived from the data.
        series: { borderWidth: 0, clip: false },
        bar: {
          borderRadius: BAR_BORDER_RADIUS,
          stacking: 'percent',
          groupPadding: 0.12,
          pointPadding: 0.02,
          // Set outright rather than through barPointWidth, which only ever hands back the deck's
          // shared 15. Nothing can be squeezed here: the chart's height is derived from this.
          pointWidth: thickness,
          dataLabels: {
            // Always on, unlike every other module: the percentages *are* this chart. Hiding the
            // question text down the left still leaves a readable distribution; hiding the figures
            // leaves five coloured blocks and a legend.
            enabled: true,
            format: '{y:.0f}%',
            // The prototype's `.seglab`: the figures sit *inside* a 15px bar, so they take the
            // deck's smallest size rather than its label size.
            style: { fontSize: '9px', fontWeight: '600', textOutline: 'none' },
            // A sliver has no room for its own figure, and a label spilling onto its neighbour is
            // worse than none -- the tooltip still carries it.
            filter: { property: 'percentage', operator: '>', value: MIN_LABELLED_PERCENT },
          },
        },
      },
      series: spine.map((option, index) => ({
        type: 'bar' as const,
        name: option.label,
        colorIndex: index,
        color: roles[index],
        // Positional against the spine: a question missing this option contributes a zero rather
        // than shifting every segment after it onto the wrong colour.
        data: drawn.map(s => {
          const match = s.points.find(point => point.label === option.label);
          return { y: match?.percent ?? 0, custom: { count: match?.value ?? 0 } };
        }),
      })) as Highcharts.SeriesOptionsType[],
    });
  }

  /**
   * How the keys share the row: the gap between them, and how many rows they end up taking.
   *
   * Sized from the answers themselves rather than by giving each an equal slice of the card. An
   * equal slice makes "Agree" as wide as "Neither agree nor disagree", which leaves the short ones
   * marooned in white space -- so the text takes what it needs and only the slack is shared out.
   *
   * `fixedGap` skips the sharing out, for a legend that starts from an edge rather than the middle.
   */
  private legendFit(labels: string[], fixedGap?: number): { gap: number; rows: number } {
    const font = this.fontFamily();
    const widths = labels.map(label =>
      Math.min(LEGEND_ITEM_WIDTH, textWidth(label, LEGEND_FONT_PX, font)));
    const chrome = LEGEND_SYMBOL_PX + LEGEND_SYMBOL_PADDING;
    const row = this.plotWidth();

    const slack = row - widths.reduce((total, width) => total + width + chrome, 0);
    const gap = fixedGap ?? Math.round(Math.max(MIN_LEGEND_GAP,
      Math.min(MAX_LEGEND_GAP, slack / Math.max(1, labels.length))));

    // Counted the way Highcharts lays them out, with the gap carried by every item -- so a row that
    // is going to wrap is known about here, while the chart's height is still being decided.
    let rows = 1;
    let used = 0;
    for (const width of widths) {
      const item = width + chrome + gap;
      // Highcharts adds the box padding after testing for a break, so break a little early.
      if (used > 0 && used + item > row - 8) {
        rows += 1;
        used = item;
      } else {
        used += item;
      }
    }
    return { gap, rows };
  }

  /**
   * One bar's bands, walked over the shared scale rather than the bar's own points, so every bar
   * bands the same options in the same order even where one of them never had an answer at an end.
   */
  private bandsFor(series: ChartSeries, scale: ChartPoint[], spec: BandSpec[] | undefined): Band[] | null {
    const own = scale.map(option => ({
      ...option,
      percent: series.points.find(point => point.label === option.label)?.percent ?? 0,
    }));
    return bandsOf(own, spec);
  }

  /**
   * The best band minus the worst for one bar -- the dial's net, by the same function. The middle
   * is excluded rather than split: those are the fence-sitters, and counting them either way would
   * flatter or punish the result.
   */
  private netOf(
    series: ChartSeries, scale: ChartPoint[], spec: BandSpec[] | undefined, topFirst: boolean,
  ): number | null {
    const bands = this.bandsFor(series, scale, spec);
    return bands ? netOfBands(bands, topFirst) : null;
  }

  /** One bar's share of one band, counted from the good end -- the band a single bar headlines. */
  private shareOf(
    series: ChartSeries, scale: ChartPoint[], spec: BandSpec[] | undefined, topFirst: boolean,
    index: number | undefined,
  ): Band | null {
    const bands = this.bandsFor(series, scale, spec);
    return bands ? shareOfBand(bands, topFirst, index) : null;
  }

  /**
   * The net as it is printed. Signed only when it is negative, matching the single-KPI dial, so the
   * same kind of figure is written the same way wherever a deck shows one.
   *
   * It was always signed, on the grounds that an unsigned 8 beside a -8 reads as the same figure.
   * The chip answers that already: it is green at or above the threshold and red below, so the
   * direction is carried by the thing the eye reaches first, and a leading + on top of it is a
   * character that changes nothing.
   */
  private netFor(
    series: ChartSeries, scale: ChartPoint[], spec: BandSpec[] | undefined, topFirst: boolean,
  ): string {
    const net = this.netOf(series, scale, spec, topFirst);
    return net === null ? '' : String(Math.round(net));
  }
}

/**
 * Sits the net column's heading over the first net chip, on the same line as the legend. Measured
 * off what was actually drawn rather than computed: the column starts wherever its chips start, and
 * the legend's baseline is the legend's own business.
 */
function headNetColumn(this: Highcharts.Chart) {
  const title = netTitle(this);
  const right = netColumnRight(this);
  if (!title || right === null) return;

  // Right-aligned on the chips rather than left: a heading like "Net Purchase Intent" is wider than
  // the column it heads, and growing leftwards runs it over the empty band beside the legend, where
  // growing rightwards runs it off the edge of the chart and out of sight.
  title.attr({ x: right, y: legendBaseline(this) });
}

/** The chips' shared right edge, relative to the chart container. */
function netColumnRight(chart: Highcharts.Chart): number | null {
  // Read off the ticks rather than the axis's label group: the chips are HTML, so they live in
  // Highcharts' HTML overlay and the SVG group they would otherwise sit in measures as empty.
  const axis = chart.xAxis?.[1] as unknown as
    { ticks?: Record<string, { label?: { element?: Element } }> } | undefined;
  const edges = Object.values(axis?.ticks ?? {})
    .map(tick => tick.label?.element?.getBoundingClientRect())
    .filter((box): box is DOMRect => !!box && box.width > 0)
    .map(box => box.right);
  if (!edges.length) return null;

  return Math.round(Math.max(...edges) - chart.container.getBoundingClientRect().left);
}

function netTitle(chart: Highcharts.Chart): Highcharts.SVGElement | undefined {
  return (chart.xAxis?.[1] as Highcharts.Axis & { axisTitle?: Highcharts.SVGElement })?.axisTitle;
}

/**
 * The y an axis title must be given to sit on the legend's baseline. Worked out as a correction to
 * where the title already is, because `attr('y')` on a text element is its baseline and the two
 * fonts need not agree on where that falls -- so the honest measurement is the gap between the two
 * rendered boxes. Idempotent: once corrected, the gap is zero and it stays put.
 */
function legendBaseline(chart: Highcharts.Chart): number {
  // Under the bars, the legend's line is nowhere near the column's head; the heading takes the band
  // opened for it above the plot instead.
  if (chart.legend?.options?.verticalAlign === 'bottom') return chart.plotTop - NET_TITLE_LIFT;

  const title = netTitle(chart);
  const item = chart.legend?.allItems?.[0] as { legendItem?: { label?: Highcharts.SVGElement } };
  const label = item?.legendItem?.label?.element;
  if (!title || !label) return chart.plotTop - NET_TITLE_LIFT;

  const titleBox = title.element.getBoundingClientRect();
  const labelBox = label.getBoundingClientRect();
  if (!titleBox.height || !labelBox.height) return chart.plotTop - NET_TITLE_LIFT;

  return Math.round(Number(title.attr('y')) + (labelBox.bottom - titleBox.bottom));
}

/** A share as the column prints it: the figure, and the word after it. */
function shareText(band: Band | null, caption: string | undefined): { figure: string; caption: string } {
  if (!band) return { figure: '', caption: '' };
  return {
    figure: `${Math.round(band.share)}%`,
    caption: caption?.trim() || band.label.trim(),
  };
}

/**
 * One share, in the net column's place. The figure in ink and the word after it in the muted grey,
 * with no chip: a share has no threshold to be read against, so it gets no red or green either.
 * The same height as the bar, so it sits level with its own.
 */
function shareLabel(text: { figure: string; caption: string } | undefined, height: number): string {
  if (!text?.figure) return '';
  return `<span style="display:block;height:${height}px;line-height:${height}px;white-space:nowrap;`
    + `font-size:${LEGEND_FONT_PX}px;font-weight:600;color:var(--ecr-ink, #26414c)">`
    + `${escapeHtml(text.figure)}`
    + (text.caption
      ? ` <span style="font-weight:400;color:var(--ecr-mut, #5a6b73)">${escapeHtml(text.caption)}</span>`
      : '')
    + '</span>';
}

/** The caption is typed by whoever writes the deck, and this label is HTML. */
function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * One net, in its chip. Red and green rather than the deck's palette on purpose: the colour here is
 * the reading of the number, and a palette colour would say nothing while looking as though it did.
 */
function netChip(text: string, threshold: number, height: number): string {
  const { background, colour } = netColours(text, threshold);

  // One fixed width and the bar's own height, so the column reads as the bars continuing past the
  // axis rather than as badges parked next to them.
  return `<span style="display:block;box-sizing:border-box;width:${NET_CHIP_WIDTH_PX}px;`
    + `height:${height}px;line-height:${height}px;border-radius:${NET_CHIP_RADIUS_PX}px;`
    + `font-size:${LEGEND_FONT_PX}px;font-weight:600;text-align:center;`
    + `background:${background};color:${colour}">${text}</span>`;
}

/** Green at or above the threshold, red below. */
function netColours(text: string, threshold: number): { background: string; colour: string } {
  const value = Number(text);
  const good = Number.isFinite(value) && value >= threshold;
  return {
    background: good ? NET_GOOD_BG : NET_BAD_BG,
    colour: good ? 'var(--nav-success)' : 'var(--nav-error)',
  };
}
